import { useRef, useState } from "react";
import { Divider, MenuItem, ListItemIcon, ListItemText } from "@mui/material";
import { useActive, useCommands } from "@remirror/react";

import { CmTableExtension } from "../extensions/table-extension";

import BorderClearIcon from "@mui/icons-material/BorderClear";
import FormatColorFillIcon from "@mui/icons-material/FormatColorFill";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import TableRowsIcon from "@mui/icons-material/TableRows";
import CallMergeIcon from "@mui/icons-material/CallMerge";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import FormatColorResetIcon from "@mui/icons-material/FormatColorReset";
import {
  CommandButton,
  DropdownButton,
  CommandMenuItem,
} from "@remirror/react-ui";
import { ColorPickerPopover } from "../../ColorPicker";

export const TableButtons = () => {
  const activeTable = useActive<CmTableExtension>().table();
  const commands = useCommands<CmTableExtension>();
  const [bgColorAnchor, setBgColorAnchor] = useState<HTMLElement | null>(null);
  const [borderColorAnchor, setBorderColorAnchor] =
    useState<HTMLElement | null>(null);
  const cellBgRef = useRef<HTMLLIElement>(null);
  const cellBorderRef = useRef<HTMLLIElement>(null);

  return (
    <>
      {!activeTable && (
        <CommandButton
          commandName="createTable"
          label="Create table"
          active={activeTable}
          enabled={true}
          icon="table2"
          onSelect={() =>
            commands.createTable({
              rowsCount: 2,
              columnsCount: 2,
            })
          }
        />
      )}
      {activeTable && (
        <DropdownButton
          aria-label="Modify table"
          icon="table2"
          color="secondary"
        >
          <CommandMenuItem
            commandName="addTableColumnAfter"
            label="Add column"
            enabled={true}
            icon="insertColumnRight"
            onSelect={commands.addTableColumnAfter}
          />

          <CommandMenuItem
            commandName="addTableRowAfter"
            label="Add row"
            enabled={true}
            icon="insertRowBottom"
            onSelect={commands.addTableRowAfter}
            onClick={() => {}}
          />
          <CommandMenuItem
            commandName="deleteTableColumn"
            label="Delete column"
            enabled={true}
            icon="deleteColumn"
            onSelect={commands.deleteTableColumn}
          />
          <CommandMenuItem
            commandName="deleteTableRow"
            label="Delete row"
            enabled={true}
            icon="deleteRow"
            onSelect={commands.deleteTableRow}
          />
          <CommandMenuItem
            commandName="deleteTable"
            label="Delete table"
            enabled={true}
            icon="deleteBinLine"
            onSelect={commands.deleteTable}
          />
          <Divider />
          <CommandMenuItem
            commandName="toggleTableBorder"
            label="Toggle border"
            enabled={true}
            icon={<BorderClearIcon fontSize="small" />}
            onSelect={commands.toggleTableBorder}
          />
          <CommandMenuItem
            commandName="toggleTableHeaderRow"
            label="Toggle header row"
            enabled={true}
            icon={<TableRowsIcon fontSize="small" />}
            onSelect={commands.toggleTableHeaderRow}
          />
          <Divider />
          <MenuItem
            ref={cellBgRef}
            onClick={() => setBgColorAnchor(cellBgRef.current)}
          >
            <ListItemIcon>
              <FormatColorFillIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Cell background</ListItemText>
          </MenuItem>
          <ColorPickerPopover
            color=""
            setColor={(color) => {
              commands.setTableCellBackground(color);
              setBgColorAnchor(null);
            }}
            anchor={bgColorAnchor}
            onClose={() => setBgColorAnchor(null)}
          />
          <MenuItem
            ref={cellBorderRef}
            onClick={() => setBorderColorAnchor(cellBorderRef.current)}
          >
            <ListItemIcon>
              <BorderColorIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Cell border color</ListItemText>
          </MenuItem>
          <ColorPickerPopover
            color=""
            setColor={(color) => {
              commands.setTableCellBorderColor(color);
              setBorderColorAnchor(null);
            }}
            anchor={borderColorAnchor}
            onClose={() => setBorderColorAnchor(null)}
          />
          <MenuItem
            onClick={() => {
              commands.clearTableCellBackground();
              commands.clearTableCellBorderColor();
              commands.setTableCellTextAlign(null);
            }}
          >
            <ListItemIcon>
              <FormatColorResetIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Clear cell styling</ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => commands.setTableCellTextAlign("left")}>
            <ListItemIcon>
              <FormatAlignLeftIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Align left</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => commands.setTableCellTextAlign("center")}>
            <ListItemIcon>
              <FormatAlignCenterIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Align center</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => commands.setTableCellTextAlign("right")}>
            <ListItemIcon>
              <FormatAlignRightIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Align right</ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => commands.mergeTableCells()}>
            <ListItemIcon>
              <CallMergeIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Merge cells</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => commands.splitTableCell()}>
            <ListItemIcon>
              <CallSplitIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Split cell</ListItemText>
          </MenuItem>
          <Divider />
          <CommandMenuItem
            commandName="createTable"
            label="Add nested table"
            enabled={true}
            icon="table2"
            onSelect={() =>
              commands.createTable({
                rowsCount: 2,
                columnsCount: 2,
              })
            }
            displayDescription={false}
          />
        </DropdownButton>
      )}
    </>
  );
};
